import React, { useState, useRef, FocusEvent } from 'react';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../../../Common/InputText';
import Toggle from '../../../Common/Toggle/Toggle';
import './ProjectDetails.scss';
import Modal61 from '../../../Common/Modal/Modal61';
import { isValidUrl } from '../../../../helpers/validation';
import { InfoWindowBody, InfoWindowTopicTitle, InfoWindowTopicBody, InfoWindowIconDescription, InfoWindowIcon } from './styles';

interface DetailsProps {
	projectDetails: any;
	onFormChange: (name: string, value: any) => void;
	onSubmit: (e: React.FormEvent) => void;
}

const MAX_NUM_PROJECT_RESOURCES = 9;
const MAX_NUM_PROJECT_EPICDATES = 4;

const ProjectDetails = ({ projectDetails, onFormChange, onSubmit }: DetailsProps) => {
	const { editProject, projectManagers, supportAgents, isFormValid, projectTags } = projectDetails;

	const [editIds, setEditIds] = useState<Array<number>>([]);	// The ids of existing links that are currently in edit mode
	const [isSubmitting, setIsSubmitting] = useState(false);

	const infoWindow = useRef();

	const onFormInputChange = (e) => {
		onFormChange(e.target.name, e.target.value);
	};

	const onSelectBoxChange = (id, name) => {
		onFormChange(name, id);
	};

	const onMultiSelectBoxChange = (csv, name) => {
		const newValue = csv ? csv.split(',') : [];
		onFormChange(name, newValue);
	};

	const onToggleChange = (name, value) => {
		onFormChange(name, value);
	};

	const onAddEpicDate = () => {
		const projectEpicLabels = editProject.projectEpicLabels.slice();

		if (projectEpicLabels.length < MAX_NUM_PROJECT_EPICDATES) {
			projectEpicLabels.push({
				id: 0,
				name: '',
				isActive: true,
				ordinal: projectEpicLabels.length,
			});
		}
		onFormChange('projectEpicLabels', projectEpicLabels);
	};

	const onEpicDateChange = (epicDate, index, field, e) => {
		const projectEpicLabels = editProject.projectEpicLabels ? editProject.projectEpicLabels.slice() : [];
		epicDate[field] = e.target.value;
		projectEpicLabels.splice(index, 1, epicDate);
		onFormChange('projectEpicLabels', projectEpicLabels);
	};

	const isLinkInEdit = (id: number) => {
		return id === 0 || editIds.some(x => x === id);
	};

	const addLink = () => {

		const newLink = {
			id: 0,
			name: '',
			url: '',
			isActive: true,
			ordinal: editProject.resources.length
		};
		const updatedLinks = [...editProject.resources];
		updatedLinks.push(newLink);

		onFormChange('resources', updatedLinks);
	};

	const onRemoveLink = (id: number) => {
		// Cannot remove new links
		const newList = editProject.resources.filter((link) => link.id !== id);

		onFormChange('resources', newList);
	};

	const onEditLink = (id: number) => {
		const updatedList = [...editIds, id];
		setEditIds(updatedList);
	};

	const onLinkInputUrlBlur = (e: FocusEvent<HTMLInputElement>, index: number) => {

		if (e.target.value === '')
			return;

		// For invalid input show error hint & change class on input
		const hintElem = document.querySelector(`[data-link-id="${index}"]`);
		if (hintElem) {
			if (!isValidUrl(`https://${e.target.value}`)) {
				e.target.className = "form-control is-invalid";
				hintElem.style.display = "inline";
			} else {
				e.target.className = "form-control";
				hintElem.style.display = "none";
			}
		}
	};

	const onLinkInputChange = (e, index) => {
		let { name, value } = e.target;

		const newList = editProject.resources.slice();

		if (name === 'url' && value) {
			// Keep the url's scheme
			const url = newList[index].url;
			value = url ? `${getUrlScheme(url)}${value}` : `https://${value}`
		}

		newList[index][name] = value;

		onFormChange('resources', newList);
	};

	const onSubmitProjectDetails = async (e) => {
		e.preventDefault();

		if (!isFormValid || isSubmitting)
			return;

		setIsSubmitting(true);

		await onSubmit(e);
		setEditIds([]);
		setIsSubmitting(false);
	};

	const openInfoWindow = async () => {
		infoWindow.current.show();
	};

	const closeInfoWindow = async () => {
		infoWindow.current.hide();
	};

	const getUrlScheme = (url: string) => {
		if (!url) {
			return 'https://';
		}

		try {
			const u = new URL(url);
			return `${u.protocol}//`;
		} catch (_) {
			return 'https://';
		}
	};

	const getUrlNoScheme = (url: string) => {
		if (!url) {
			return '';
		}

		try {
			return url.replace(/(^\w+:|^)\/\//, '');
		} catch (_) {
			return 'https://';
		}
	};

	return (
		<div className='container project-details'>
			<form name='projectDetailsForm' className={isFormValid ? '' : 'was-validated'} onSubmit={onSubmitProjectDetails}>
				<div className='row me-3 mb-3 pt-3' id='collapseTaskName'>
					<div className='col-6'>
						<div>
							<label htmlFor='name' className='title'>
								Project Name
							</label>
							<InputText
								name='name'
								placeholder='Project Name'
								type='text'
								value={editProject.name}
								onChange={onFormInputChange}
								isRequired={true}
								errorMessage='Project Name is required'
								maxLength={50}
								hintText='Character Limit 50'
							/>

						</div>
					</div>
					<div className='col-3'>
						<div>
							<label htmlFor='task-description' className='title'>
								Project Abbreviation
							</label>
							<InputText
								name='abbreviation'
								placeholder='Abbreviation'
								type='text'
								value={editProject.abbreviation}
								onChange={onFormInputChange}
								isRequired={true}
								errorMessage='Abbreviation is required'
								maxLength={10}
								hintText='Character Limit 10'
							/>
						</div>
					</div>
					<div className='col-2'>
						<div className='status'>
							<div className='status-badge active'>
								<div className='status-indicator'></div>
							</div>
							<div className='status-text'>Active Project</div>
						</div>
						<Toggle name='isActive' text='' checked={editProject.isActive} onChange={onToggleChange} />
					</div>
					<div className='col-1 mt-4'>
						<button type='submit' className='btn btn-primary' disabled={isSubmitting}>
							Save
						</button>
					</div>
				</div>
				<div className='row me-3' id='collapseTaskName'>
					<div className='col-4'>
						<div className='mb-3'>
							<label htmlFor='name' className='title'>
								Project Description
							</label>
							<InputText
								name='description'
								placeholder='Description'
								type='text'
								value={editProject.description}
								onChange={onFormInputChange}
								isRequired={false}
								maxLength={600}
							/>
						</div>
					</div>
					<div className='col-4'>
						<div className='mb-3'>
							<label htmlFor='communityId' className='title'>
								Default Project Support Agent
							</label>
							<MultiSelectBox
								name='supportAgentId'
								idField='id'
								valueField='name'
								options={supportAgents}
								onChange={onSelectBoxChange}
								selectedValues={editProject.supportAgentId?.toString() ?? ''}
								isRequired={true}
								errorMessage='Support Agent is required'
								isMulti={false}
							/>
							<div className='milestone'>
								<div className='row d-flex justify-content-between'>
									<div className='col'>
										<small id='task-name-help' className='form-text text-muted sub-title'>
											This person is assigned to new support tickets for this project
										</small>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className='col-4'>
						<div className='mb-3'>
							<label htmlFor='communityId' className='title'>
								Project Implementation Manager
							</label>
							<MultiSelectBox
								name='projectManagerId'
								idField='id'
								valueField='name'
								options={projectManagers}
								onChange={onSelectBoxChange}
								selectedValues={editProject.projectManagerId?.toString() ?? ''}
								isRequired={true}
								errorMessage='Project Manager is required'
								isMulti={false}
							/>
							<div className='milestone'>
								<div className='row d-flex justify-content-between'>
									<div className='col'>
										<small id='task-name-help' className='form-text text-muted sub-title'>
											This can be a specific user or team
										</small>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='row me-3' id='collapseTaskName'>
					<div className='col-4'>
						<div className='mb-3'>
							<label htmlFor='communityId' className='title'>
								Tags
							</label>
							<MultiSelectBox
								name='projectTagIds'
								options={projectTags}
								idField='id'
								valueField='name'
								selectedValues={editProject.projectTagIds?.toString() ?? ''}
								onChange={onMultiSelectBoxChange}
								isMulti={true}
							/>
						</div>
					</div>
					<div className='col-4'>
						<div className='mb-3'>
							<label htmlFor='name' className='title'>
								Zero Date Name
							</label>
							<InputText
								name='zeroDateName'
								placeholder='Zero Date Name'
								type='text'
								value={editProject.zeroDateName}
								onChange={onFormInputChange}
								isRequired={true}
								errorMessage='Zero Date Name is required'
								maxLength={100}
							/>
						</div>
					</div>
					<div className='col-4'></div>
				</div>

				<br />
				<br />

				<div className='divider-container'>
					<div className='divider-line' />
				</div>

				<br />
				<br />
				<div className='row me-3 mb-3' id='collapseTaskName'>
					<div className='col-4'>
						<div className='card'>
							<div className='card-header d-flex justify-content-between'>
								<div>
									<span className="links-header">Links</span>
									{/* <button type='button' className='btn btn-link' onClick={openInfoWindow} title='What are links?'>
										<i className='fa-regular fa-info-circle fa-lg'></i>
									</button> */}
								</div>

								{(editProject.resources && editProject.resources.length < MAX_NUM_PROJECT_RESOURCES) &&
									<button type='button' className='btn btn-link' onClick={addLink}>
										<i className='fa-regular fa-circle-plus'></i>
										<span className='mx-1'>Add Link</span>
									</button>
								}
							</div>

							<div className='card-body'>

								<div className="links-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

									{(editProject.resources && editProject.resources.length)
										? editProject.resources.map((link, index) =>
											!link.isFile && (
												isLinkInEdit(link.id)
													? (
														<div key={index} className='d-flex' style={{ gap: '10px' }}>
															<div className="text-center" style={{ flex: '1 1 0px' }}>
																<div className='input-group'>
																	<span className="input-group-text">{getUrlScheme(link.url)}</span>
																	<input
																		type="text"
																		className="form-control"
																		name='url'
																		id={`linkUrl_${index}`}
																		value={getUrlNoScheme(link.url)}
																		onChange={e => onLinkInputChange(e, index)}
																		onBlur={e => onLinkInputUrlBlur(e, index)} />
																</div>
																<span className="url-error-hint" data-link-id={index}>Please enter a valid link.</span>
															</div>

															<input
																name='name'
																id="linkName"
																type='text'
																placeholder='Name your link'
																className='form-control'
																style={{ flex: '1 1 0px' }}
																value={link.name}
																onChange={(e) => onLinkInputChange(e, index)}
															/>
														</div>
													)
													: (
														<div
															key={index}
															className='link-row'
															style={{
																display: 'flex',
																justifyContent: 'space-between',
																alignItems: 'center',
																width: '100%',
															}}>
															<div className='link' style={{ flex: 1 }}>
																{link.name}
															</div>
															<div className='link-actions'>
																<div className='icon' onClick={() => onEditLink(link.id)}>
																	<i className='far fa-lg fa-pen-to-square ms-2'></i>
																</div>
																<div className='icon icon-trash' onClick={() => onRemoveLink(link.id)}>
																	<span className='far fa-lg fa-trash-can ms-2'></span>
																</div>
															</div>
														</div>
													)
											)
										)
										: <div className='link-row text-center'>No links yet, let's add some!</div>
									}
								</div>
							</div>
						</div>
					</div>
					<div className='col-4'>
						{editProject.isProjectReferenceDatesActive && (
							<>
								<div className='row align-items-center justify-content-between mb-2'>
									<h4 className='project-title'>Epics</h4>
									<div className='what-is-link'>
										<button type='button' className='btn btn-link' onClick={openInfoWindow}>
											<i className='fa-regular fa-info-circle fa-lg'></i>
											&nbsp;What are Epics?
										</button>
									</div>
								</div>
								<div className='mb-3'>
									{editProject.projectEpicLabels && editProject.projectEpicLabels.length > 0 ? (
										editProject.projectEpicLabels.map((epicDate, index) => {
											return (
												<div key={epicDate.ordinal ?? index}>
													<label htmlFor='name' className='title'>
														{`Epic Name ${index + 1}`}
													</label>
													<InputText
														name={`epicDateName${index + 1}`}
														placeholder={`Epic Name ${index + 1}`}
														type='text'
														value={epicDate.name}
														onChange={(e) => onEpicDateChange(epicDate, index, 'name', e)}
														isRequired={false}
														maxLength={50}
													/>
												</div>
											);
										})
									) : (
										<div>
											<label htmlFor='name' className='title'>
												Epic Name 1
											</label>
											<InputText
												name={`epicDateName1`}
												label={`Epic Name 1`}
												placeholder={`Epic Name 1`}
												type='text'
												value=''
												onChange={(e) => onEpicDateChange({}, 0, 'name', e)}
												isRequired={false}
												maxLength={50}
											/>
										</div>
									)}

									{(editProject.projectEpicLabels?.length || 0) < MAX_NUM_PROJECT_EPICDATES && (
										<p>
											<button type='button' className='btn btn-link epic-button' onClick={onAddEpicDate}>
												<i className='fa-regular fa-circle-plus'></i>
												<span className='mx-1'>Add Additional Epic for this Project</span>
											</button>
										</p>
									)}
								</div>
							</>
						)}
					</div>
					<div className='col-4'>
						{editProject.isProjectWaiversActive && (
							<div className='row align-items-center justify-content-between mb-2'>
								<h4 className='project-title'>Waivers</h4>
								<div className='what-is-link'>
									<button type='button' className='btn btn-link' onClick={openInfoWindow}>
										<i className='fa-regular fa-info-circle fa-lg'></i>
										&nbsp;What are Project Waivers?
									</button>
								</div>
								<div className='toggle-container'>
									<Toggle
										name='canRequestProjectWaiver'
										text='Allow Locations to Request Project Waiver'
										checked={editProject.canRequestProjectWaiver}
										onChange={onToggleChange}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</form>

			<Modal61
				ref={infoWindow}
				body={
					<InfoWindowBody>
						<div className='row justify-content-center'>
							<InfoWindowIcon>
								<i className='fa-regular fa-paper-plane'></i>
							</InfoWindowIcon>
						</div>
						<div className='row justify-content-center'>
							<InfoWindowIconDescription>Project Details</InfoWindowIconDescription>
						</div>

						<InfoWindowTopicTitle>What are Project Resources?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							Here you can provide any links that would help teams complete the project. These links are available to everyone
						</InfoWindowTopicBody>

						<InfoWindowTopicTitle>What are Epics?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							You can highlight other important dates in your project, which we call Epics. You can identify Epic names per project and
							then these additional dates can be set per Location within the Project to highlight a key point in time. For example, if
							your Project is for Openings, you might want to notate the date the Location&apos;s website will be activated, or the date
							construction should be substantially completed. You&apos;ll establish Epic name(s) for the Project, and then in Location
							Actions, you&apos;ll be able to enter a date for the Epic for each Location during the pushdown process alongside the Zero
							Date.
						</InfoWindowTopicBody>

						<InfoWindowTopicTitle>What are Project Waivers?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							The Project Waiver feature, when turned on, will allow locations to request to be exempt from completing this specific
							project. When the waiver is requested, the project is put on hold until the request is approved or denied. If approved,
							the project will remain on hold. If denied, the project will be reinstated at the location. The project manager will
							receive a waiver request via the Project Manager Dashboard to then decide if the request will be approved or denied.
							Emails are also sent to the Project Waiver requestor, and the Project Implementation Manager for this specific project.
						</InfoWindowTopicBody>
					</InfoWindowBody>
				}
				customFooter={
					<>
						<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeInfoWindow}>
							Go to Project Details Page
						</button>
					</>
				}
				title={''}
				primaryButtonText={''}
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={closeInfoWindow}
				isLoading={false}
				isCentered={true}
			/>
		</div>
	);
};

export default ProjectDetails;
